import React from 'react';
import { Card, CardContent, Badge, Button } from '@ncoder/ui';
import { Globe, Settings, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

interface DeploymentCardProps {
  name: string;
  environment: string;
  status: 'active' | 'inactive' | 'deploying';
  version: string;
  endpoint: string;
  traffic: string;
  lastDeployed: string;
  uptime: string;
  requestsPerMin: number;
  onSettings?: () => void;
  onViewLogs?: () => void;
}

export const DeploymentCard: React.FC<DeploymentCardProps> = ({
  name,
  environment,
  status,
  version,
  endpoint,
  traffic,
  lastDeployed,
  uptime,
  requestsPerMin,
  onSettings,
  onViewLogs,
}) => {
  const getStatusIcon = () => {
    if (status === 'active') return <CheckCircle className="h-3 w-3 mr-1" />;
    if (status === 'deploying') return <Clock className="h-3 w-3 mr-1" />;
    return <AlertTriangle className="h-3 w-3 mr-1" />;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-lg truncate">{name}</h3>
              <p className="text-sm text-muted-foreground truncate">{endpoint}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                  {getStatusIcon()}
                  {status}
                </Badge>
                <Badge variant="outline">{environment}</Badge>
                <span className="text-xs text-muted-foreground">{version}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onSettings}>
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onViewLogs}>
              View Logs
            </Button>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Traffic</p>
              <p className="font-medium">{traffic}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Uptime</p>
              <p className="font-medium">{uptime}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Requests/min</p>
              <p className="font-medium">{requestsPerMin}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Deployed</p>
              <p className="font-medium">{lastDeployed}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
